// Aktivitätsprotokoll für Objekt und Einheit – zusammengesetzt aus vorhandenen Quellen
// (Notizen, Dokumente, Tickets, Termine, Übergaben, Mietbeginn/-ende, Änderungen an Mietern).
// Ein eigenes Protokoll je Objekt gibt es nicht; entity_audit erfasst nur Kontakte.

import { auditZuAktivitaet, zuAktivitaet } from '../kontakte/aktivitaet';
import type { Aktivitaet } from '../kontakte/types';

export type { Aktivitaet };

export interface AktivitaetQuellen {
  notizen?: readonly { id: string; text: string; created_at: string }[];
  dokumente?: readonly { id: string; titel: string | null; datei_name: string | null; kategorie: string | null; created_at: string }[];
  tickets?: readonly { id: string; titel: string | null; status: string | null; created_at: string }[];
  termine?: readonly { id: string; titel: string | null; status: string | null; start_datum: string }[];
  uebergaben?: readonly { id: string; art: string; status: string; durchgefuehrt_am: string; einheit?: string | null }[];
  mieter?: readonly { id: string; name: string; mietbeginn: string | null; mietende: string | null; einheit?: string | null }[];
  audit?: readonly { id: string; aktion: string; feld: string | null; alt_wert: string | null; neu_wert: string | null; geaendert_am: string; name?: string }[];
}

const UEBERGABE: Record<string, string> = { einzug: 'Einzugsprotokoll', auszug: 'Auszugsprotokoll', kauf: 'Übergabe (Kauf)' };

/** Alle Ereignisse bis heute (Termine nur vergangene), neueste zuerst. */
export function baueAktivitaeten(q: AktivitaetQuellen, jetztIso: string, limit = 60): Aktivitaet[] {
  const heute = jetztIso.slice(0, 10);
  const liste: Aktivitaet[] = [
    ...(q.notizen ?? []).map(n => zuAktivitaet('notiz', n)),
    ...(q.dokumente ?? []).map(d => zuAktivitaet('dokument', d)),
    ...(q.tickets ?? []).map(t => zuAktivitaet('ticket', t)),
    ...(q.termine ?? []).filter(t => t.start_datum <= jetztIso && (t.status ?? '') !== 'abgesagt').map(t => ({ ...zuAktivitaet('termin', t), ziel: { typ: 'termin', id: t.id } })),
    ...(q.uebergaben ?? []).filter(u => u.status === 'abgeschlossen').map(u => ({
      typ: 'dokument' as const, datum: u.durchgefuehrt_am, titel: UEBERGABE[u.art] ?? 'Übergabe', detail: u.einheit ?? null, ziel: null, id: `ueb-${u.id}`,
    })),
    ...(q.mieter ?? []).flatMap(m => [
      ...(m.mietbeginn && m.mietbeginn.slice(0, 10) <= heute ? [{ typ: 'aenderung' as const, datum: m.mietbeginn, titel: `Einzug ${m.name}`, detail: m.einheit ?? null, ziel: null, id: `ein-${m.id}` }] : []),
      ...(m.mietende && m.mietende.slice(0, 10) <= heute ? [{ typ: 'aenderung' as const, datum: m.mietende, titel: `Auszug ${m.name}`, detail: m.einheit ?? null, ziel: null, id: `aus-${m.id}` }] : []),
    ]),
    ...(q.audit ?? []).map(a => {
      const x = auditZuAktivitaet(a);
      return a.name ? { ...x, titel: `${a.name}: ${x.titel}` } : x;
    }),
  ];
  return liste
    .filter(a => !!a.datum)
    .sort((a, b) => (b.datum ?? '').localeCompare(a.datum ?? ''))
    .slice(0, limit);
}
